/**
 * Toast Service Bridge
 * This module provides a way to dispatch toast notifications from Redux slices
 * without directly depending on React hooks.
 */

// Define the toast event type
type ToastEventType = 'success' | 'error' | 'warning' | 'info';

// Define the toast event interface
interface ToastEvent {
  type: ToastEventType;
  headerMessage: string;
  message?: string;
}

// Global toast event listeners
const listeners: Array<(event: ToastEvent) => void> = [];

/**
 * Register a listener for toast events
 * This should be called from the ToastServiceProvider
 */
export const registerToastEventListener = (listener: (event: ToastEvent) => void): (() => void) => {
  listeners.push(listener);

  // Return a function to unregister the listener
  return () => {
    const index = listeners.indexOf(listener);
    if (index !== -1) {
      listeners.splice(index, 1);
    }
  };
};

/**
 * Dispatch a toast event
 * This can be called from anywhere, including Redux slices
 */
const dispatchToastEvent = (event: ToastEvent): void => {
  listeners.forEach((listener) => listener(event));
};

/**
 * Show a success toast
 */
export const showSuccessToast = (headerMessage: string, message?: string): void => {
  dispatchToastEvent({
    type: 'success',
    headerMessage,
    message,
  });
};

/**
 * Show an error toast
 */
export const showErrorToast = (headerMessage: string, message?: string): void => {
  dispatchToastEvent({
    type: 'error',
    headerMessage,
    message,
  });
};

/**
 * Show a warning toast
 */
export const showWarningToast = (headerMessage: string, message?: string): void => {
  dispatchToastEvent({
    type: 'warning',
    headerMessage,
    message,
  });
};

/**
 * Show an info toast
 */
export const showInfoToast = (headerMessage: string, message?: string): void => {
  dispatchToastEvent({
    type: 'info',
    headerMessage,
    message,
  });
};

/**
 * For Redux slices where direct toast service is not available
 * This fallback will use alert() if no listeners are registered
 */
export const showWarningAlert = (message: string): void => {
  if (listeners.length === 0) {
    // No listeners registered, fall back to alert
    alert(message);
  } else {
    // Listeners registered, show toast
    showWarningToast('Warning', message);
  }
};
